import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _map from 'lodash/map';
import _toString from 'lodash/toString';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import _filter from 'lodash/filter';
import PropTypes from 'prop-types';
// Core components
import Checkbox from 'components/core/Checkbox';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mCompany from 'models/company';
import mDoor from 'models/door';
import mEntity from 'models/entity';
import mInstallation from 'models/installation';
import mProduct from 'models/product';
// Custom
import {
  entityDataShape,
  companiesShape,
  doorDataShape,
} from 'constants/PropTypesShapes';
import { buildFormErrors, entityConstraints } from 'utils/Forms';
import { sanitizeInt } from 'utils/Tools';
import withFormManagement from 'hocs/WithFormManagement';
import Callout from 'components/misc/Callout';
import NumberInputField from 'components/inputFields/NumberInputField';
import ItemsSelectField from 'components/inputFields/ItemsSelectField';
import CompanyItem from 'companies/assets/main/CompanyItem';
import DoorItem from 'installations/assets/configuration/DoorItem';

class EntityAddEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  handleDoorChange(door) {
    const {
      fields: { excludedDoors },
      onFieldsChange,
    } = this.props;

    const doorIncluded = _includes(excludedDoors, door.id);
    let newExcludedDoors = [];

    if (!doorIncluded) {
      newExcludedDoors = [...excludedDoors, door.id];
    } else {
      newExcludedDoors = _filter(excludedDoors, (id) => id !== door.id);
    }

    onFieldsChange({
      excludedDoors: newExcludedDoors,
    });
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      entity, fields: {
        name, publicName, product, capacity, companyId, isOnDemand, excludedDoors, allowListingOneparkBookings,
      },
      onErrorsChange,
      onValidate,
    } = this.props;

    const errors = buildFormErrors(
      {
        name: name || (entity && mEntity.isMaster(entity) ? 'Master' : ''),
        companyId,
      },
      entityConstraints,
    );
    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate(
        {
          allowListingOneparkBookings,
          name,
          publicName,
          product,
          capacity,
          companyId,
          isOnDemand,
          excludedDoors,
        },
        entity,
      );
      return true;
    }
    return false;
  }

  render() {
    const {
      fields: {
        name, publicName, product, capacity, companyId, isOnDemand, excludedDoors, allowListingOneparkBookings,
      },
      errors,
      entity,
      companies,
      onFieldsChange,
      installationDoors,
    } = this.props;
    const sortedDoors = mDoor.sortElements(installationDoors);
    const sortedCompanies = mCompany.sortElements(companies.elements);
    const isAdminEntity = entity && mInstallation.isAdminEntity(entity);
    const isCpmEntity = product === mProduct.getConfigurationCpmValue;
    // Retrive company
    const entityCompany = _find(companies.flattenElements, ['id', companyId]);

    return (
      <div className="row">
        {isAdminEntity ? (
          <div className="col-12">
            <Typography type="subtitle">
              <LocalizedMessage
                id="info.installationEntityName"
                values={{
                  isMaster: mEntity.isMaster(entity),
                  hostsOneparkBookings: mEntity.hostsOneparkBookings(entity),
                  name: entity.name,
                }}
              />
            </Typography>
          </div>
        ) : (
          <React.Fragment>
            <div className="col-6">
              <TextField
                label={<LocalizedMessage id="field.name" />}
                errorText={_get(errors, 'name')}
                value={name}
                required
                margin="dense"
                onChange={(value) => onFieldsChange({ name: value })}
              />
            </div>
            <div className="col-6">
              {isCpmEntity && (
                <TextField
                  label={<LocalizedMessage id="field.publicName" />}
                  errorText={_get(errors, 'publicName')}
                  value={publicName}
                  margin="dense"
                  onChange={(value) => onFieldsChange({ publicName: value })}
                />
              )}
            </div>
            {!isAdminEntity && (
              <OnlyWith
                permissions={{
                  needed: permissions.pAdmin,
                }}
              >
                <div className="col-12 col-md-4 mt-2">
                  <ItemsSelectField
                    labelId="field.product"
                    value={product}
                    errorText={_get(errors, 'product')}
                    items={mProduct.configurationList}
                    disabled={!!entity}
                    required
                    margin="dense"
                    onChange={(value) => onFieldsChange({ product: value, profilePermissions: [] })}
                  />
                </div>
              </OnlyWith>
            )}
            <div className="col-12 col-md-4 mt-2">
              <NumberInputField
                label={<LocalizedMessage id="field.capacity" />}
                errorText={_get(errors, 'capacity')}
                min={0}
                value={capacity}
                margin="dense"
                sanitizer={sanitizeInt}
                onChange={(value) => onFieldsChange({ capacity: value })}
              />
            </div>
            {isCpmEntity && (
              <div className="col-12 col-md-4 mt-4">
                <Checkbox
                  label={<LocalizedMessage id="field.isOnDemand" />}
                  checked={isOnDemand}
                  margin="normal"
                  onChange={(checked) => onFieldsChange({ isOnDemand: checked })}
                />
              </div>
            )}
            {isCpmEntity && (
              <div className="col-12 mt-4">
                <Checkbox
                  label={<LocalizedMessage id="field.displayOneparkBooking" />}
                  checked={allowListingOneparkBookings}
                  margin="normal"
                  onChange={(checked) => onFieldsChange({ allowListingOneparkBookings: checked })}
                />
              </div>
            )}
          </React.Fragment>
        )}
        {/* ----- Excluded Doors ----- */}
        {/* if sortedDoors TODO */}
        {isCpmEntity && !_isEmpty(installationDoors) && (
          <React.Fragment>
            <div className="col-12 pt-4">
              <Callout>
                <LocalizedMessage id="title.excludedDoors" />
              </Callout>
            </div>
            <div className="col-12 px-3">
              <div className="row pb-3">
                {_map(sortedDoors, (door) => (
                  <div key={_toString(door.id)} className="col-12">
                    <DoorItem
                      door={door}
                      toggle={{
                        toggled: _includes(excludedDoors, door.id),
                        onToggle: () => this.handleDoorChange(door),
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </React.Fragment>
        )}

        {/* ----- Choose a company ----- */}
        <div className="col-12 pt-4">
          <Callout>
            <LocalizedMessage id={entity ? 'title.company' : 'title.chooseCompany'} />
          </Callout>
        </div>
        {_has(errors, 'companyId') && (
          <div className="col-12 pt-3">
            <Callout severity="error" withInfoIcon>
              {errors.companyId}
            </Callout>
          </div>
        )}
        {entity ? (
          <div className="col-12 pt-3">{entityCompany && entityCompany.name}</div>
        ) : (
          !!sortedCompanies
          && _map(sortedCompanies, (company) => (
            <div key={_toString(company.id)} className="col-12 col-md-6 pt-3">
              <CompanyItem
                company={company}
                withSelection
                selectedId={companyId}
                onSelect={(value) => onFieldsChange({ companyId: value })}
              />
            </div>
          ))
        )}
      </div>
    );
  }
}

EntityAddEditForm.propTypes = {
  entity: entityDataShape,
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  companies: companiesShape.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  installationDoors: PropTypes.arrayOf(doorDataShape),
};

EntityAddEditForm.defaultProps = {
  entity: null,
  installationDoors: null,
  errors: null,
};

const mapStateToProps = (state) => ({
  companies: state.company.list,
  installationDoors: state.installationDoor.list.elements,
});

const mapFieldsToProps = {
  allowListingOneparkBookings: {
    resourceKey: 'allow_listing_of_onepark_bookings',
    defaultValue: false,
  },
  name: {
    resourceKey: 'name',
    defaultValue: '',
  },
  publicName: {
    resourceKey: 'public_name',
    defaultValue: '',
  },
  product: {
    resourceKey: 'product',
    defaultValue: mProduct.configurationList[0].value,
  },
  capacity: {
    resourceKey: 'capacity',
    defaultValue: 0,
  },
  companyId: {
    resourceKey: 'company_id',
    defaultValue: null,
  },
  isOnDemand: {
    resourceKey: 'is_on_demand',
    defaultValue: false,
  },
  excludedDoors: {
    resourceKey: 'excluded_doors',
    defaultValue: [],
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.entity,
});

module.exports = compose(
  connect(mapStateToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
)(EntityAddEditForm);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/entities/EntityAddEditForm.js